import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

const PIX_KEY_TYPE_LABEL: Record<string, string> = {
  cpf: 'CPF',
  email: 'E-mail',
  phone: 'Telefone',
  random: 'Chave aleatória',
};

/** P19 · Config da conta. Repasse é sempre 7,99% flat pra todo mundo — não é um ajuste por
 *  loja, então não entra aqui. A chave PIX é a única coisa do KYC que o parceiro pode corrigir
 *  depois do onboarding (tudo o mais — CPF/CNPJ, termo aceito, status de revisão — fica fora). */
export function Ajustes() {
  const qc = useQueryClient();
  const kyc = useQuery({ queryKey: ['kyc'], queryFn: () => api.kyc() });
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('email');
  const [savedNote, setSavedNote] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (kyc.data) {
      setPixKey(kyc.data.pixKey);
      setPixKeyType(kyc.data.pixKeyType);
    }
  }, [kyc.data]);

  const save = useMutation({
    mutationFn: () => api.updatePixKey({ pixKey, pixKeyType }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kyc'] });
      setSavedNote(true);
      setError(null);
    },
    onError: (e: unknown) => {
      setSavedNote(false);
      setError(e instanceof Error ? e.message : 'Não foi possível salvar a chave PIX');
    },
  });

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Ajustes</h1>
        <p>Repasse e chave PIX do seu cadastro.</p>
      </div>

      <section>
        <div className={ui.secH}>
          <h2>Repasse</h2>
        </div>
        <div className={ui.card} style={{ padding: '18px 20px' }}>
          <p className={ui.hint}>
            A Storeo fica com <b>7,99%</b> de cada venda, sem taxa fixa — igual pra todo mundo. Não é algo que
            dá pra ajustar por loja.
          </p>
        </div>
      </section>

      <section>
        <div className={ui.secH}>
          <h2>Chave PIX</h2>
        </div>
        <div className={ui.card} style={{ padding: '18px 20px' }}>
          {kyc.isLoading ? (
            <p className={ui.empty}>Carregando…</p>
          ) : kyc.isError ? (
            <p className={ui.error}>Não foi possível carregar sua chave PIX.</p>
          ) : (
            <form
              className={ui.form}
              onSubmit={(e) => {
                e.preventDefault();
                setSavedNote(false);
                save.mutate();
              }}
            >
              <div className={ui.formRow}>
                <label className={ui.field}>
                  Tipo
                  <select value={pixKeyType} onChange={(e) => setPixKeyType(e.target.value)}>
                    {Object.entries(PIX_KEY_TYPE_LABEL).map(([v, label]) => (
                      <option key={v} value={v}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={ui.field}>
                  Chave
                  <input required value={pixKey} onChange={(e) => setPixKey(e.target.value)} />
                </label>
              </div>
              <span className={ui.hint}>É pra essa chave que o seu repasse cai — confira antes de salvar.</span>
              {error ? <p className={ui.error}>{error}</p> : null}
              {savedNote ? <p className={ui.hint}>Chave PIX atualizada.</p> : null}
              <div className={ui.actions}>
                <button className={`${ui.btn} ${ui.btnPrimary}`} type="submit" disabled={save.isPending}>
                  {save.isPending ? 'Salvando…' : 'Salvar chave PIX'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
